import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { usePrivacy } from "../../context/PrivacyContext";
import { useLang } from "../../context/LangContext";
import { getLenis } from "../../hooks/useLenis";

const POLICY = {
  pl: {
    title: "Polityka Prywatności",
    updated: "Ostatnia aktualizacja: 6 października 2026",
    sections: [
      {
        heading: "Administrator danych osobowych",
        body: "Administratorem Twoich danych osobowych jest Przestrzeń Apps Dominika Majerczak (jednoosobowa działalność gospodarcza), ul. Nowa 5A, 62-040 Puszczykowo, NIP 7831819315.\n\nKontakt: przestrzenapps@gmail.com",
      },
      {
        heading: "Jakie dane zbieramy?",
        body: "Za pośrednictwem formularza kontaktowego zbieramy:\n• Imię i nazwisko\n• Adres e-mail\n• Dane dotyczące projektu podane dobrowolnie w wiadomości\n\nStrona nie zbiera żadnych innych danych osobowych. Korzystamy z anonimowej analityki Plausible (bez cookies śledzących).",
      },
      {
        heading: "Cel i podstawa prawna przetwarzania",
        body: "Twoje dane przetwarzane są wyłącznie w celu udzielenia odpowiedzi na wysłane zapytanie oraz — w przypadku nawiązania współpracy — realizacji umowy.\n\nPodstawa prawna: art. 6 ust. 1 lit. b i f Rozporządzenia RODO (niezbędność do wykonania umowy lub podjęcia działań przed jej zawarciem oraz prawnie uzasadniony interes administratora).",
      },
      {
        heading: "Odbiorcy danych",
        body: "Dane z formularza kontaktowego przekazywane są do Netlify, Inc. (USA) — dostawcy usługi obsługi formularzy. Netlify stosuje standardowe klauzule umowne zapewniające odpowiedni poziom ochrony danych zgodnie z RODO.",
      },
      {
        heading: "Zewnętrzne zasoby — Google Fonts",
        body: "Strona korzysta z usługi Google Fonts (Google LLC, USA) w celu zapewnienia spójności wizualnej. Ładowanie czcionek może wiązać się z przekazaniem Twojego adresu IP do serwerów Google. Podstawa prawna: prawnie uzasadniony interes. Szczegóły: policies.google.com/privacy",
      },
      {
        heading: "Okres przechowywania danych",
        body: "Dane z formularza kontaktowego przechowywane są do czasu zakończenia korespondencji lub — w przypadku nawiązania współpracy — przez okres wymagany przepisami prawa podatkowego (5 lat).",
      },
      {
        heading: "Twoje prawa (RODO)",
        body: "Masz prawo do:\n• Dostępu do swoich danych i uzyskania ich kopii\n• Sprostowania nieprawidłowych danych\n• Usunięcia danych (\"prawo do bycia zapomnianym\")\n• Ograniczenia przetwarzania\n• Przenoszenia danych\n• Sprzeciwu wobec przetwarzania\n\nAby skorzystać ze swoich praw, skontaktuj się: przestrzenapps@gmail.com\n\nMasz też prawo do wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych: uodo.gov.pl",
      },
      {
        heading: "Analityka — Plausible",
        body: "Strona korzysta z Plausible Analytics (Plausible Insights OÜ, Estonia) w celu zbierania zagregowanych, anonimowych statystyk odwiedzin. Plausible nie używa cookies ani nie profiluje użytkowników. Podstawa prawna: prawnie uzasadniony interes. Szczegóły: plausible.io/data-policy",
      },
      {
        heading: "Pliki cookies",
        body: "Niniejsza strona nie wykorzystuje plików cookies do celów reklamowych ani śledzenia użytkowników. Netlify może stosować techniczne pliki cookies niezbędne do prawidłowego działania formularza kontaktowego.",
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    updated: "Last updated: 6 October 2026",
    sections: [
      {
        heading: "Data Controller",
        body: "The data controller is Przestrzeń Apps Dominika Majerczak (sole proprietorship), ul. Nowa 5A, 62-040 Puszczykowo, Poland, tax ID (NIP) 7831819315.\n\nContact: przestrzenapps@gmail.com",
      },
      {
        heading: "What data do we collect?",
        body: "Via the contact form, we collect:\n• Full name\n• Email address\n• Project details voluntarily provided in your message\n\nThe site does not collect any other personal data. We use anonymous Plausible Analytics (no tracking cookies).",
      },
      {
        heading: "Purpose and legal basis",
        body: "Your data is processed solely to respond to your inquiry and — if a project begins — to fulfil a contract.\n\nLegal basis: Art. 6(1)(b) and (f) GDPR (necessity to perform a contract or take pre-contractual steps, and legitimate interest of the controller).",
      },
      {
        heading: "Data recipients",
        body: "Contact form data is transferred to Netlify, Inc. (USA) — the form processing provider. Netlify applies standard contractual clauses to ensure an adequate level of data protection under GDPR.",
      },
      {
        heading: "External resources — Google Fonts",
        body: "This site uses Google Fonts (Google LLC, USA) for visual consistency. Loading fonts may involve transferring your IP address to Google servers. Legal basis: legitimate interest. Details: policies.google.com/privacy",
      },
      {
        heading: "Data retention",
        body: "Contact form data is retained until the correspondence is concluded or — if a project is initiated — for the period required by tax law (5 years).",
      },
      {
        heading: "Your rights (GDPR)",
        body: "You have the right to:\n• Access your data and obtain a copy\n• Rectify inaccurate data\n• Erasure (\"right to be forgotten\")\n• Restriction of processing\n• Data portability\n• Object to processing\n\nTo exercise your rights: przestrzenapps@gmail.com\n\nYou also have the right to lodge a complaint with the Polish Data Protection Authority: uodo.gov.pl",
      },
      {
        heading: "Analytics — Plausible",
        body: "This site uses Plausible Analytics (Plausible Insights OÜ, Estonia) to collect aggregated, anonymous visit statistics. Plausible does not use cookies or profile users. Legal basis: legitimate interest. Details: plausible.io/data-policy",
      },
      {
        heading: "Cookies",
        body: "This site does not use cookies for advertising or tracking. Netlify may use technical cookies necessary for the contact form to function correctly.",
      },
    ],
  },
};

function PolicyBody({ text }) {
  return (
    <div className="font-body text-sm leading-relaxed" style={{ color: "rgba(242,232,216,0.62)" }}>
      {text.split("\n").map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        if (line.startsWith("•")) {
          return (
            <p key={i} className="flex gap-2 items-start mt-1">
              <span style={{ color: "#C87941", flexShrink: 0 }}>•</span>
              <span>{line.slice(1).trim()}</span>
            </p>
          );
        }
        return <p key={i} className="mt-0">{line}</p>;
      })}
    </div>
  );
}

export default function PrivacyPolicyModal() {
  const { open, closePolicy } = usePrivacy();
  const { lang } = useLang();
  const policy = POLICY[lang];

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") closePolicy(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closePolicy]);

  useEffect(() => {
    const lenis = getLenis();
    if (open) lenis?.stop();
    else lenis?.start();
    return () => lenis?.start();
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="privacy-overlay"
          className="fixed inset-0 flex items-end sm:items-center justify-center px-4 py-6 sm:py-10"
          style={{ zIndex: 9500, background: "rgba(10,9,7,0.88)", backdropFilter: "blur(7px)" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={closePolicy}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-modal-title"
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl overflow-y-auto rounded-2xl"
            style={{
              maxHeight: "88vh",
              background: "#13120E",
              border: "1px solid rgba(200,121,65,0.18)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.03)",
            }}
          >
            {/* Sticky header */}
            <div
              className="sticky top-0 flex items-center justify-between px-7 py-5"
              style={{
                background: "rgba(19,18,14,0.97)",
                borderBottom: "1px solid rgba(242,232,216,0.06)",
                backdropFilter: "blur(12px)",
                zIndex: 1,
              }}
            >
              <div>
                <h2
                  id="privacy-modal-title"
                  className="font-display text-xl sm:text-2xl font-light text-white-warm"
                  style={{ letterSpacing: "0.02em" }}
                >
                  {policy.title}
                </h2>
                <p
                  className="font-mono text-[9px] tracking-[0.22em] uppercase mt-1"
                  style={{ color: "rgba(242,232,216,0.28)" }}
                >
                  {policy.updated}
                </p>
              </div>
              <button
                onClick={closePolicy}
                aria-label={lang === "pl" ? "Zamknij" : "Close"}
                className="flex items-center justify-center rounded-lg transition-all duration-200 shrink-0"
                style={{
                  width: 36, height: 36,
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "rgba(242,232,216,0.45)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(200,121,65,0.45)";
                  e.currentTarget.style.color = "#C87941";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                  e.currentTarget.style.color = "rgba(242,232,216,0.45)";
                }}
              >
                <X size={14} strokeWidth={1.75} />
              </button>
            </div>

            {/* Sections */}
            <div className="px-7 py-7 space-y-7">
              {policy.sections.map((section, i) => (
                <div key={i}>
                  <h3
                    className="font-display text-base font-normal mb-2.5"
                    style={{ color: "#C87941", letterSpacing: "0.01em" }}
                  >
                    {section.heading}
                  </h3>
                  <PolicyBody text={section.body} />
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-7 pb-7">
              <div className="h-px" style={{ background: "rgba(242,232,216,0.06)" }} />
              <p
                className="font-mono text-[9px] tracking-[0.22em] uppercase mt-5 text-center"
                style={{ color: "rgba(242,232,216,0.18)" }}
              >
                Przestrzeń Apps · Poznań
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
